import type {
    CreatureSizeCompileInput,
    CreatureSizeCompileResult,
    CreatureSizeDirectSetEvent,
    CreatureSizeEvent,
    CreatureSizeSource,
    CreatureSizeStepChangeEvent,
} from '../types';
import { readItemUuidSlot } from './inventory-raw-readers';

export const CREATURE_SIZE_IDS = Object.freeze([
    '13456FEC-A2EE-DB46-5829-0EB2F627E1B7',
    'AAC1F466-7A8B-C593-1ADC-FC402B8909D7',
    'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC',
    '52FFDB16-A991-C8D4-FE59-7C75EE6CF7AD',
    'D0EF6D7A-82BC-1E6E-9D36-5004C7E0561C',
    'CEA2946E-D395-46EA-A6E7-74D21EA2376E',
    'C94CDD74-8ACD-26B0-5C53-4CA29CF21D68',
    '4C33FD22-5382-15DA-1C46-F57F14378AFD',
] as const);

export const TINY_CREATURE_SIZE_ID = CREATURE_SIZE_IDS[0];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
    return typeof value === 'string' && value.trim() !== '' && UUID_PATTERN.test(value.trim());
}

/** Size cells use the same scalar, sparse-slot and rich-shell reader as authored inventory layers. */
export const creatureSizeUuidFromCell = readItemUuidSlot;

/** Later authored classification cells take precedence; invalid/empty cells do not erase an earlier size. */
export function canonicalizeCreatureSizeCells(cells: readonly unknown[]): string | null {
    let chosen = '';
    for (const cell of cells) {
        const candidate = creatureSizeUuidFromCell(cell);
        if (candidate !== '') chosen = candidate;
    }
    return chosen === '' ? null : chosen;
}

/** Mirrors change_creature_size_by_UUID(), including clamping and unknown-id stability. */
export function shiftCreatureSizeId(currentSizeId: string, change: number): string {
    const currentIndex = CREATURE_SIZE_IDS.indexOf(currentSizeId as typeof CREATURE_SIZE_IDS[number]);
    if (currentIndex < 0) return currentSizeId;
    const nextIndex = Math.max(0, Math.min(currentIndex + Math.trunc(change), CREATURE_SIZE_IDS.length - 1));
    return CREATURE_SIZE_IDS[nextIndex]!;
}

function eventSource(source: CreatureSizeSource): Pick<CreatureSizeEvent, 'source_id' | 'source_label' | 'source_kind'> {
    return {
        source_id: source.sourceId,
        source_label: source.sourceLabel,
        ...(source.sourceKindIsDerivable && isUuid(source.sourceId) ? {} : { source_kind: source.sourceKind }),
    };
}

/**
 * Pure Slice 2 compile island. Source collection stays outside this boundary; callers provide the
 * already ordered direct sets and step fragments that recalc_creature_size() walks and batches.
 */
export function compileCreatureSize(input: CreatureSizeCompileInput): CreatureSizeCompileResult {
    const canonicalBase = canonicalizeCreatureSizeCells(input.baseSizeCells);
    const baseSizeId = canonicalBase ?? TINY_CREATURE_SIZE_ID;
    let directCurrent = baseSizeId;
    const events: CreatureSizeEvent[] = [];

    for (const directSet of input.directSets) {
        const toSizeId = creatureSizeUuidFromCell(directSet.sizeId) || directCurrent;
        if (toSizeId === directCurrent) continue;
        const event: CreatureSizeDirectSetEvent = {
            kind: 'direct_set',
            from_size_id: directCurrent,
            to_size_id: toSizeId,
            ...eventSource(directSet),
        };
        events.push(event);
        directCurrent = toSizeId;
    }

    const preBatchSizeId = directCurrent;
    let running = 0;
    for (const step of input.stepChanges) {
        const delta = Math.trunc(step.delta);
        if (delta === 0) continue;
        const fromSizeId = shiftCreatureSizeId(preBatchSizeId, running);
        running += delta;
        const event: CreatureSizeStepChangeEvent = {
            kind: 'step_change',
            delta,
            from_size_id: fromSizeId,
            to_size_id: shiftCreatureSizeId(preBatchSizeId, running),
            ...eventSource(step),
        };
        events.push(event);
    }

    const finalSizeId = running === 0 ? preBatchSizeId : shiftCreatureSizeId(preBatchSizeId, running);
    const breakdown = {
        base: isUuid(input.ancestryId)
            ? { size_id: baseSizeId, source_id: input.ancestryId }
            : {
                size_id: baseSizeId,
                source_label: 'Base (ancestry / sub_type)',
                source_kind: 'ancestral',
            },
        final_size_id: finalSizeId,
        ...(events.length > 0 ? { events } : {}),
    };

    return { sizeId: finalSizeId, breakdown };
}
